import { useState, useEffect } from "react";
import { TopAdminNav } from "../layouts/TopAdminNav";
import BackButton from "../components/backButton";
import { CircleLoader } from "../components/CircleLoader";
import { defaultUrl } from "../features/actions";
import { makeFetchRequest, makePostRequest } from "../utilities/common";
import { LayoutGrid, UserCheck, Sparkles, PlusCircle } from "lucide-react"; // Using Lucide for modern icons

export const AddClass = () => {
  const [loading, setLoading] = useState(false);
  const [addClass, setAddClass] = useState({
    name: "",
    teacher_id: "",
    assessment_mode: "standard_matrix",
  });
  const [getTeacher, setGetTeacher] = useState([]);

  const handleAddClass = ({ target: { name, value } }: any) => {
    setAddClass({ ...addClass, [name]: value });
  };

  const handleClassSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    const mode = addClass.assessment_mode || "standard_matrix";
    const body = {
      name: addClass.name.trim(),
      assessment_mode: mode,
      teachers: addClass.teacher_id && [addClass.teacher_id.trim()],
    };
    await makePostRequest(`${defaultUrl}/classroom/create`, body);
    setLoading(false);
    setAddClass({ name: "", teacher_id: "", assessment_mode: "standard_matrix" });
  };

  async function queryTeacher() {
    const response = await makeFetchRequest(
      `${defaultUrl}/teachers`,
      false,
      true,
    );
    setGetTeacher(response);
  }

  useEffect(() => {
    queryTeacher();
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-20">
      {/* TOP NAVIGATION AREA */}
      <div className="w-full flex items-center">
        <BackButton />
        <TopAdminNav name="Jane Doe" title="Classroom Management" />
      </div>

      <main className="max-w-5xl mx-auto mt-12 px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* LEFT SIDE: STATS/INFO CARD */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-slate-900 rounded-[2rem] p-8 text-white shadow-2xl relative overflow-hidden group">
              <Sparkles className="absolute top-[-10px] right-[-10px] text-white/10 w-32 h-32 group-hover:rotate-12 transition-transform duration-700" />
              <div className="relative z-10">
                <p className="text-slate-400 font-medium text-sm mb-1 uppercase tracking-widest">
                  Global Overview
                </p>
                <h2 className="text-4xl font-black mb-6">Class Creation</h2>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6">
              <h4 className="text-blue-900 font-bold mb-2 flex items-center gap-2 text-sm">
                <PlusCircle size={16} /> Quick Tip
              </h4>
              <p className="text-blue-700/80 text-sm leading-relaxed">
                Assigning a lead teacher now helps in automated result
                processing and attendance tracking.
              </p>
            </div>
          </div>

          {/* RIGHT SIDE: THE FORM */}
          <div className="lg:col-span-8">
            <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 p-10">
              <header className="mb-10">
                <h3 className="text-2xl font-bold text-slate-900">
                  Class Details
                </h3>
                <p className="text-slate-500">
                  Provide the fundamental information for the new classroom.
                </p>
              </header>

              <form onSubmit={handleClassSubmit} className="space-y-8">
                {/* Input Field */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 ml-1">
                    Classroom Name
                  </label>
                  <div className="relative group">
                    <input
                      type="text"
                      placeholder="e.g. Grade 10 - Platinum"
                      name="name"
                      value={addClass.name}
                      onChange={handleAddClass}
                      className="w-full h-14 px-5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/5 transition-all font-medium text-slate-900 placeholder:text-slate-400"
                    />
                  </div>
                </div>

                {/* Select Field */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 ml-1 flex items-center gap-2">
                    <UserCheck size={16} className="text-slate-400" /> Lead
                    Teacher
                  </label>
                  <select
                    name="teacher_id"
                    value={addClass.teacher_id}
                    onChange={handleAddClass}
                    className="w-full h-14 px-5 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/5 transition-all font-medium text-slate-900 appearance-none"
                  >
                    <option value="">Select a staff member</option>
                    {getTeacher?.map((user: any, idx: number) => (
                      <option key={idx} value={user?.id}>
                        {user?.surname} {user?.firstname}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Assessment Mode Selector */}
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-slate-700 ml-1">
                    Assessment & Grading Mode
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() =>
                        setAddClass({ ...addClass, assessment_mode: "standard_matrix" })
                      }
                      className={`p-4 rounded-2xl border-2 text-left transition-all ${
                        addClass.assessment_mode !== "traits_only"
                          ? "border-[#0096D1] bg-sky-50/50 shadow-xs"
                          : "border-slate-200 bg-white hover:bg-slate-50 opacity-80"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-slate-800 text-sm">
                          Standard Matrix
                        </span>
                        {addClass.assessment_mode !== "traits_only" && (
                          <span className="text-[10px] font-bold bg-[#0096D1] text-white px-2 py-0.5 rounded-full">
                            Selected
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed">
                        Full academic subject exams, continuous assessment CA, and behavioral metrics.
                      </p>
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        setAddClass({ ...addClass, assessment_mode: "traits_only" })
                      }
                      className={`p-4 rounded-2xl border-2 text-left transition-all ${
                        addClass.assessment_mode === "traits_only"
                          ? "border-amber-500 bg-amber-50/60 shadow-xs"
                          : "border-slate-200 bg-white hover:bg-slate-50 opacity-80"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-slate-800 text-sm">
                          Traits Only
                        </span>
                        {addClass.assessment_mode === "traits_only" && (
                          <span className="text-[10px] font-bold bg-amber-500 text-white px-2 py-0.5 rounded-full">
                            Selected
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed">
                        For Creche, Nursery & Early Years: observational traits, milestones & remarks only.
                      </p>
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-6">
                  <button
                    type="submit"
                    disabled={loading || !addClass.name}
                    className="w-full h-16 bg-slate-900 hover:bg-black text-white rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed group"
                  >
                    {loading ? (
                      <CircleLoader />
                    ) : (
                      <>
                        Create Class
                        <PlusCircle
                          size={20}
                          className="group-hover:rotate-90 transition-transform"
                        />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
